import { useEffect, useState } from "react";
import { Plus, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { formatPrice } from "../../lib/utils";

const EMPTY_FORM = { name: "", sku: "", price: "", stock_quantity: 0, low_stock_threshold: 5 };

export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adjusting, setAdjusting] = useState({});

  const load = () =>
    api
      .get("/owner/products")
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/owner/products", form);
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const adjustStock = async (product, delta) => {
    setAdjusting((a) => ({ ...a, [product.id]: true }));
    try {
      await api.post(`/owner/products/${product.id}/stock`, {
        quantity_delta: delta,
        reason: delta > 0 ? "restock" : "sale",
      });
      load();
    } finally {
      setAdjusting((a) => ({ ...a, [product.id]: false }));
    }
  };

  const deactivate = async (id) => {
    await api.delete(`/owner/products/${id}`);
    load();
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Inventory</h1>
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4" /> Add product
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6 p-4">
          <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input placeholder="Product name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input placeholder="SKU (optional)" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
            <Input placeholder="Price" type="number" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            <Input placeholder="Starting stock" type="number" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} />
            <Input placeholder="Low stock threshold" type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} />
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save product"}</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : products.length === 0 ? (
        <p className="text-muted-foreground">No products yet — add your first one above.</p>
      ) : (
        <div className="divide-y divide-border rounded-2xl border border-border">
          {products.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
              <div>
                <p className="font-medium text-slate-900">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.sku ? `SKU ${p.sku} · ` : ""}{formatPrice(p.price)}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {p.is_low_stock && (
                  <Badge variant="warning"><AlertTriangle className="mr-1 h-3 w-3" /> Low stock</Badge>
                )}
                <span className="font-semibold text-slate-900">{p.stock_quantity} in stock</span>
                <Button size="sm" variant="outline" disabled={adjusting[p.id]} onClick={() => adjustStock(p, -1)}>-1</Button>
                <Button size="sm" variant="outline" disabled={adjusting[p.id]} onClick={() => adjustStock(p, 1)}>+1</Button>
                {p.is_active && (
                  <Button size="sm" variant="ghost" onClick={() => deactivate(p.id)}>Deactivate</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
